import React, { FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Status } from '../../../../types/redux/data/dataTypes';
import { RSFundPageModel } from '../../../../api/api.types';
import { AxiosInstance } from '../../../../utilities/requestClient';
import { getStoredAggregatedReportURL, LiquidityPages } from '../../../../api/rs-hg/liquidity/liquidity.routes';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { CustomColumn } from '../../../../types/components/tables/tableTypes';
import FixedRstTable from '../../liquidity/rstFixed/components/fixedRstTable.component';
import {
  addCommasToNumbersAndRound,
  percentageToTwoDecimalPlaces,
} from '../../../../utilities/numberFormatters';
import RstCell, { RstStatus } from './RstCell';
import RaptorStatusBox from '../../../feedback/RaptorStatusBox.component';
import { specifyStatus } from '../../../../utilities/generalMappings';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';

export interface AifRestrictionsUnderRedemptionsData {
  scenario: number;
  nav: number;
  market: Status;
  counterparty: Status;
  credit: Status;
  marketLiquidity: Status;
  fundingLiquidity: Status;
}

interface TimeSeriesDataUnderRedemptionsData {
  scenario: number;
  nav: number;
  valueAtRisk: number;
  correlation: number;
  ssd: number;
}

interface StatusBreakdownData {
  scenario: number;
  market: AifmdData;
  counterparty: AifmdData;
  credit: AifmdData;
  marketLiquidity: AifmdData;
  fundingLiquidity: AifmdData;
}

type AifmdData = {
  current: RstStatus;
  forecast: RstStatus;
  scenario: RstStatus;
  stress: RstStatus;
};

const statusBreakdownColumns: CustomColumn<StatusBreakdownData>[] = [
  {
    title: 'Scenario',
    field: 'scenario',
    width: 1,
    cellStyle: {
      textAlign: 'center',
    },
    headerStyle: {
      textAlign: 'center',
    },
  },
  {
    title: 'Market',
    field: 'market',
    render: (rowData) => <RstCell {...rowData.market} />,
    width: '20%',
    cellStyle: {
      padding: 0,
      verticalAlign: 'initial',
    },
    headerStyle: {
      textAlign: 'center',
    },
  },
  {
    title: 'Counterparty',
    field: 'counterparty',
    render: (rowData) => <RstCell {...rowData.counterparty} />,
    width: '20%',
    cellStyle: {
      padding: 0,
      verticalAlign: 'initial',
    },
    headerStyle: {
      textAlign: 'center',
    },
  },
  {
    title: 'Credit',
    field: 'credit',
    render: (rowData) => <RstCell {...rowData.credit} />,
    width: '20%',
    cellStyle: {
      padding: 0,
      verticalAlign: 'initial',
    },
    headerStyle: {
      textAlign: 'center',
    },
  },
  {
    title: 'Market Liquidity',
    field: 'marketLiquidity',
    render: (rowData) => <RstCell {...rowData.marketLiquidity} />,
    width: '20%',
    cellStyle: {
      padding: 0,
      verticalAlign: 'initial',
    },
    headerStyle: {
      textAlign: 'center',
    },
  },
  {
    title: 'Funding Liquidity',
    field: 'fundingLiquidity',
    render: (rowData) => <RstCell {...rowData.fundingLiquidity} />,
    width: '20%',
    cellStyle: {
      padding: 0,
      verticalAlign: 'initial',
    },
    headerStyle: {
      textAlign: 'center',
    },
  },
];

const aifRestrictionsColumns: CustomColumn<AifRestrictionsUnderRedemptionsData>[] =
  [
    {
      title: 'Scenario',
      field: 'scenario',
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
    },
    {
      title: 'NAV',
      field: 'nav',
      render: (rowData) => addCommasToNumbersAndRound(rowData.nav),
      cellStyle: {
        textAlign: 'right',
      },
      headerStyle: {
        textAlign: 'center',
      },
      width: '9.38rem',
    },
    {
      title: 'Market',
      field: 'market',
      render: (rowData: AifRestrictionsUnderRedemptionsData) => (
        <RaptorStatusBox status={specifyStatus(rowData.market)} />
      ),
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
    },
    {
      title: 'Counterparty',
      field: 'counterparty',
      render: (rowData: AifRestrictionsUnderRedemptionsData) => (
        <RaptorStatusBox status={specifyStatus(rowData.counterparty)} />
      ),
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
    },
    {
      title: 'Credit',
      field: 'credit',
      render: (rowData: AifRestrictionsUnderRedemptionsData) => (
        <RaptorStatusBox status={specifyStatus(rowData.credit)} />
      ),
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
    },
    {
      title: 'Market Liquidity',
      field: 'marketLiquidity',
      render: (rowData: AifRestrictionsUnderRedemptionsData) => (
        <RaptorStatusBox status={specifyStatus(rowData.marketLiquidity)} />
      ),
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
    },
    {
      title: 'Funding Liquidity',
      field: 'fundingLiquidity',
      render: (rowData: AifRestrictionsUnderRedemptionsData) => (
        <RaptorStatusBox status={specifyStatus(rowData.fundingLiquidity)} />
      ),
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
    },
  ];

const timeSeriesDataColumns: CustomColumn<TimeSeriesDataUnderRedemptionsData>[] =
  [
    {
      title: 'Scenario',
      field: 'scenario',
      width: 1,
    },
    {
      title: 'NAV',
      field: 'nav',
      render: (rowData) => addCommasToNumbersAndRound(rowData.nav),
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'right',
      },
      width: '9.38rem',
    },
    {
      title: 'VaR',
      field: 'valueAtRisk',
      render: (rowData) => percentageToTwoDecimalPlaces(rowData.valueAtRisk),
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
    },
    {
      title: 'Correlation',
      field: 'correlation',
      render: (rowData) => percentageToTwoDecimalPlaces(rowData.correlation),
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
    },
    {
      title: 'SSD',
      field: 'ssd',
      render: (rowData) => percentageToTwoDecimalPlaces(rowData.ssd),
      headerStyle: {
        textAlign: 'center',
      },
      cellStyle: {
        textAlign: 'center',
      },
    },
  ];
enum TableId {
  AIF_RESTRICTIONS = 'aifRestrictions',
  TIME_SERIES = 'timeSeries',
  STATUS_BREAKDOWN = 'statusBreakdown',
}
const areaKeyMap = {
  Counterparty: 'counterparty',
  Credit: 'credit',
  'Funding Liquidity': 'fundingLiquidity',
  Market: 'market',
  'Market Liquidity': 'marketLiquidity',
};
const tableIdMap: { [key in TableId]: keyof AifRstFixedPagePayload } = {
  aifRestrictions: 'aifmd_list',
  timeSeries: 'dynamic_list',
  statusBreakdown: 'aifmd_data',
};

/** Inner `data` on stored aggregated `liquidity_reverse_stress_test` report. */
export type AifRstFixedPagePayload = {
  aifmd_list?: unknown;
  dynamic_list?: unknown;
  aifmd_data?: Record<string, unknown>;
};

export type AifRstFixedPageModel = RSFundPageModel<AifRstFixedPagePayload>;

const AIF_RST_REPORT_KEY = 'liquidity_reverse_stress_test';

function useAifRstFixedPageModel(
  fundId: string | undefined,
  positionDate?: string | null,
) {
  const url = fundId
    ? getStoredAggregatedReportURL(fundId, AIF_RST_REPORT_KEY, positionDate, LiquidityPages.LIQUIDITY_RST_FIXED_MARKET_VOLUME)
    : '';

  return useQuery<AifRstFixedPageModel | null, Error>({
    queryKey: ['aif-rst-fixed', fundId, positionDate] as const,
    queryFn: async () => {
      const { data } = await AxiosInstance.get<AifRstFixedPageModel>(url);
      return data;
    },
    enabled: !!fundId,
    staleTime: 1000 * 60 * 5,
  });
}

function buildData(
  payload: AifRstFixedPagePayload | undefined | null,
  id: TableId,
):
  | AifRestrictionsUnderRedemptionsData[]
  | TimeSeriesDataUnderRedemptionsData[]
  | StatusBreakdownData[] {
  if (!payload) return [];
  const raw = payload[tableIdMap[id]];
  if (raw == null) return [];
  try {
    const dataForUse: any = Array.isArray(raw) ? [...raw] : { ...raw };
    // remove the header row
    if (Array.isArray(dataForUse)) {
      dataForUse.shift();
    }
    switch (id) {
      case TableId.AIF_RESTRICTIONS:
        return dataForUse.map((datum: any) => {
          const [
            scenario,
            nav,
            market,
            counterparty,
            credit,
            marketLiquidity,
            fundingLiquidity,
          ] = datum;
          return {
            scenario: scenario + 1,
            nav,
            market: specifyStatus(market),
            counterparty: specifyStatus(counterparty),
            credit: specifyStatus(credit),
            marketLiquidity: specifyStatus(marketLiquidity),
            fundingLiquidity: specifyStatus(fundingLiquidity),
          };
        });
      case TableId.TIME_SERIES:
        return dataForUse.map((datum: any) => {
          const [scenario, nav, valueAtRisk, correlation, ssd] = datum;
          return {
            scenario: scenario + 1,
            nav,
            valueAtRisk,
            correlation,
            ssd,
          };
        });
      case TableId.STATUS_BREAKDOWN:
        // first get the number of rows
        const numberOfRows = dataForUse.Counterparty.Current[0].length;
        const dataToReturn = [];
        /*
         * Then in each row we're going to need
         * market: {
         *
         * }
         * counterparty
         * credit
         * marketLiquidity and
         * fundingLiquidity
         *
         */
        for (let i = 0; i < numberOfRows; i++) {
          const finalRowObject: any = {};
          finalRowObject['scenario'] = i + 1;
          for (const area of [
            'Counterparty',
            'Credit',
            'Funding Liquidity',
            'Market',
            'Market Liquidity',
          ]) {
            const {
              Current: current,
              Forecast: forecast,
              Scenario: scenario,
              Stress: stress,
            } = dataForUse[area];
            // here the final dataKey is counterparty, credit etc
            //@ts-ignore
            const finalDataKey = areaKeyMap[area];
            finalRowObject[finalDataKey] = {
              current: {
                status: current[0][i],
                message: current[1][i],
              },
              forecast: {
                status: forecast[0][i],
                message: forecast[1][i],
              },
              scenario: {
                status: scenario[0][i],
                message: scenario[1][i],
              },
              stress: {
                message: stress[1][i],
              },
            };

            /*
             * Here we need counterparty =  {
             *   current:
             *   forecast
             *   scenario:
             *   stress - and for each of those keys we need an array of messages. We get this array of messages by splitting by br
             * }
             *
             */
          }
          dataToReturn.push(finalRowObject);
        }
        return dataToReturn;

      default:
        return [];
    }
  } catch (err) {
    return [];
  }
}

type AifRstFixedTablesProps = {
  payload: AifRstFixedPagePayload;
  positionDate: string;
};

const AifRstFixedTables: FC<AifRstFixedTablesProps> = ({
  payload,
  positionDate,
}) => {
  const aifData = buildData(payload, TableId.AIF_RESTRICTIONS);
  const timeSeriesData = buildData(payload, TableId.TIME_SERIES);
  const statusChangeData = buildData(payload, TableId.STATUS_BREAKDOWN);

  return (
    <>
      <FixedRstTable<AifRestrictionsUnderRedemptionsData>
        positionDate={positionDate}
        columns={aifRestrictionsColumns}
        data={aifData as AifRestrictionsUnderRedemptionsData[]}
        fixed={6}
        title="AIF Restrictions under Redemptions"
      />
      <FixedRstTable<TimeSeriesDataUnderRedemptionsData>
        positionDate={positionDate}
        columns={timeSeriesDataColumns}
        data={timeSeriesData as TimeSeriesDataUnderRedemptionsData[]}
        fixed={6}
        title="Time Series Data under Redemptions"
      />
      <FixedRstTable<StatusBreakdownData>
        positionDate={positionDate}
        columns={statusBreakdownColumns}
        data={statusChangeData as StatusBreakdownData[]}
        fixed={12}
        title="Status Breakdown"
      />
    </>
  );
};

const AifRstFixed: FC<FundInfoComponentProps> = ({ fundId, positionDate }) => {
  const pageQuery = useAifRstFixedPageModel(fundId, positionDate);

  return (
    <QueryBoundary<AifRstFixedPagePayload>
      isLoading={pageQuery.isLoading}
      isFetching={pageQuery.isFetching}
      isError={pageQuery.isError}
      error={pageQuery.error}
      data={pageQuery.data?.data}
      noDataMessage="No data available."
      errorMessage="There was an error retrieving the data. The RiskSystem administrator has been notified."
    >
      {(payload) => (
        <AifRstFixedTables
          payload={payload}
          positionDate={
            pageQuery.data?.selected_position_date ||
            pageQuery.data?.returned_position_date ||
            ''
          }
        />
      )}
    </QueryBoundary>
  );
};

export default AifRstFixed;
